import { Component, Input, OnChanges } from '@angular/core';
import { AttractionReviewSource } from '../../models/attraction.model';
import { formatRating } from '../attraction-utils';

interface HistogramRow {
  star: number;
  count: number;
  /** Share of the histogram total, 0..1 — drives the bar width. */
  fraction: number;
}

interface StarSlot {
  /** 'full' | 'half' | 'empty' — how much of this star position is filled. */
  kind: 'full' | 'half' | 'empty';
}

/**
 * Ratings: the score, its star distribution, and the per-provider split as a footer (Android
 * `RatingBreakdownCard`). The provider exposes no review text at our access tier, so the histogram —
 * real data — is the body of the card instead of quoted reviews.
 *
 * Bars are a share of the TOTAL (a lone 1-star reads as the sliver it is); the total also guards the
 * division. When there is no histogram the card is simply score + footer.
 */
@Component({
    selector: 'app-attraction-rating-breakdown',
    templateUrl: './attraction-rating-breakdown.component.html',
    styleUrls: ['./attraction-rating-breakdown.component.scss'],
    host: { class: 'attractions-scope' },
    standalone: false
})
export class AttractionRatingBreakdownComponent implements OnChanges {
  @Input() rating: number | null | undefined = null;
  @Input() reviewCount: number | null | undefined = null;
  @Input() histogram: { [star: string]: number } | null | undefined = null;
  @Input() sources: AttractionReviewSource[] | null | undefined = null;

  /** High-to-low, the order the distribution reads in. */
  private static readonly STAR_LEVELS = [5, 4, 3, 2, 1];

  stars: StarSlot[] = [];
  histogramRows: HistogramRow[] = [];
  histogramTotal = 0;
  footer = '';

  ngOnChanges(): void {
    this.stars = this.buildStars(this.rating ?? 0);
    this.buildHistogram();
    this.footer = this.buildFooter();
  }

  get formattedRating(): string {
    return formatRating(this.rating);
  }

  private buildStars(rating: number): StarSlot[] {
    // Filled low-to-high (1..5), matching Android's reversed STAR_LEVELS.
    return [1, 2, 3, 4, 5].map(star => {
      const remainder = rating - (star - 1);
      const kind: StarSlot['kind'] = remainder >= 0.75 ? 'full' : remainder >= 0.25 ? 'half' : 'empty';
      return { kind };
    });
  }

  private buildHistogram(): void {
    const counts = AttractionRatingBreakdownComponent.STAR_LEVELS.map(star => ({
      star,
      count: this.histogram?.[String(star)] ?? 0
    }));
    this.histogramTotal = counts.reduce((sum, c) => sum + c.count, 0);
    this.histogramRows = counts.map(c => ({
      star: c.star,
      count: c.count,
      fraction: this.histogramTotal > 0 ? c.count / this.histogramTotal : 0
    }));
  }

  private buildFooter(): string {
    const shown = (this.sources ?? []).filter(s => (s.total_count ?? 0) > 0);
    return shown
      .map(s => {
        const provider = s.provider ? s.provider.toLowerCase() : '';
        const name = provider ? provider.charAt(0).toUpperCase() + provider.slice(1) : '';
        return `${name} ${formatRating(s.average_rating)} (${s.total_count ?? 0})`;
      })
      .join('  ·  ');
  }

  starIcon(kind: StarSlot['kind']): string {
    return kind === 'full' ? 'star' : kind === 'half' ? 'star-half' : 'star-border';
  }
}
